import React, { Dispatch, useEffect, useReducer, useRef, useCallback, useMemo, useState } from 'react';
import store from 'store2';
import dayjs, { Dayjs } from 'dayjs';
import {
    appointmentKeyStored,
    createAppointment,
    getAppointment,
    getValuationByVin,
    IAppointment,
    IKeySlot,
    IKeyTable,
    insertAuditTrail,
    instance,
    IValuation,
    updateAppointment,
    updateDMSCustomer,
    updateDMSVehicle,
    useKeyTable,
} from '../../API/GoMoto';
import { AppointmentLoanerOrRentalOption, Events, Timezone } from '../../API/Enums';
import { useStepReducer } from '../../helpers/stepReducer';
import { useKioskTimeTable, useKioskState } from '../Kiosk/kiosk';
import { useLocale, useLocalization } from '../Localization/localization';
import { RecallsProvider } from '../recalls';
import { SavedSessionProvider } from '../SavedSession/SavedSession';
import { BackgroundDataProvider } from '../backgroundData';
import { Locales } from '../Localization/localization.types';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { GoMotoFunction } from '../../API/GoMoto.types';
import { AxiosResponse, HttpStatusCode } from 'axios';
import { useAdminState } from '../Admin/admin';
import { ActionMap } from '../../API/GoMoto.types';
import { useLocker } from '../hardware';
import { ILockerFixture, useLockerContext, useSetLockerStatus } from '../lockers';
import {
    LockerOperationHook,
    LockerOperationSteps,
    LockerActionSettings,
    KeyDropError,
    AppointmentOrLockerId,
    StartProps,
    AppointmentTimeFormats,
    AppointmentTimeFormatTypes,
    FormatAppointmentDateTimeHook,
    AppointmentHistoryHook,
    InsertLastPageVisitedParams,
    UpdateAppointmentHook,
} from './appointment.types';
import { useApiCall } from '../Axios/Axios';

type TypedInFields = {
    firstName?: string;
    lastName?: string;
    businessName?: string;
    phoneNumber?: string;
    email?: string;
};

export type InitialStateType = {
    appointment?: IAppointment;
    hasAppointment?: boolean;
    initialInteraction?: Date;
    isFullMobileCheckInAllowed?: boolean;
    QRScannedAtLocker?: boolean;
    maskPhone?: boolean;
    maskEmail?: boolean;
    maskVin?: boolean;
    typedInFields?: TypedInFields;
    gaveConsentToSignElectronically?: boolean;
    termsAcknowledged?: boolean;
    retainOldParts?: boolean;
    estimateRequest?: 'yes' | 'no' | 'onlyIf';
    repairCost?: number;
    doNotSaveSignature?: boolean;
    estimateSignature?: string | undefined;
    towIn?: boolean;
};

const initialState: InitialStateType = {};

type AppointmentProviderProps = {
    children: React.ReactNode;
    useCache?: boolean;
    appointment?: IAppointment;
    suppressBgCalls?: boolean;
    suppressRecallCalls?: boolean;
};

export enum AppointmentDispatchTypes {
    Add = 'ADD_APPOINTMENT',
    Remove = 'REMOVE',
    RemoveAndKeepMasks = 'REMOVE_AND_KEEP_MASKS',
    RemoveAndKeepInitialInteraction = 'REMOVE_AND_INITIAL_INTERACTION',
    RemoveAndRecordInitialInteraction = 'REMOVE_AND_RECORD_INTERACTION',
    Update = 'UPDATE_APPOINTMENT',
    HasAppointment = 'HAS_APPOINTMENT',
    RecordInitialInteraction = 'INITIAL_INTERACTION',
    RecordNewInitialInteraction = 'NEW_INITIAL_INTERACTION',
    AllowFullMobileCheckin = 'ALLOW_FULL_MOBILE_CHECK_IN',
    QRScannedAtLocker = 'QR_SCANNED_AT_LOCKER',
    MaskField = 'MASK_FIELD',
    UnmaskField = 'UNMASK_FIELD',
    TypedInFields = 'TYPED_IN_FIELDS',
    GaveConsentToSignElectronically = 'GAVE_CONSENT_TO_SIGN_ELECTRONICALLY',
    TermsAcknowledged = 'TERMS_ACKNOWLEDGED',
    RetainOldParts = 'RETAIN_OLD_PARTS',
    EstimateRequest = 'ESTIMATE_REQUEST',
    RepairCost = 'REPAIR_COST',
    SaveSignature = 'SAVE_SIGNATURE',
    EstimateSignature = 'ESTIMATE_SIGNATURE',
    TowInProcess = 'TOW_IN',
    RemoveAppointment = 'REMOVE_APPOINTMENT',
    SerializePreCheckIn = 'SERIALIZE_PRECHECKIN',
    DeserializePreCheckIn = 'DESERIALIZE_PRECHECKIN'
}

const AppointmentStateContext = React.createContext<InitialStateType>(initialState);
const AppointmentDispatchContext = React.createContext<Dispatch<AppointmentActions>>(() => null);

/** Creates an appointment context */
export function AppointmentProvider({ children, useCache, appointment, suppressBgCalls, suppressRecallCalls }: AppointmentProviderProps) {
    const cachedState = useCache
        ? (store.session.get('appointment', (key: string, value: any) => (key === 'initialInteraction' ? new Date(value) : value)) as InitialStateType) || {
            ...initialState,
        }
        : { ...initialState };
    if (appointment) {
        cachedState.appointment = appointment;
    }
    const [state, dispatch] = useReducer(appointmentReducer, cachedState);

    useEffect(() => {
        if (useCache) {
            store.session.set('appointment', state);
        }
    }, [state, useCache]);

    const suppressRecallsProvider: boolean = state.towIn || !!suppressRecallCalls || !!(state.appointment && state.appointment.mobileCheckInComplete);
    const suppressBackgroundDataProvider: boolean = state.towIn || !!suppressBgCalls || !!(state.appointment && state.appointment.mobileCheckInComplete);

    return (
        <AppointmentStateContext.Provider value={state}>
            <AppointmentDispatchContext.Provider value={dispatch}>
                <SavedSessionProvider>
                    <RecallsProvider suppressRecallCalls={suppressRecallsProvider}>
                        <BackgroundDataProvider suppressBackgroundDataProvider={suppressBackgroundDataProvider}>{children}</BackgroundDataProvider>
                    </RecallsProvider>
                </SavedSessionProvider>
            </AppointmentDispatchContext.Provider>
        </AppointmentStateContext.Provider>
    );
}

/** Retrieves the appointment from the closest context */
export function useAppointmentState() {
    const context = React.useContext(AppointmentStateContext);
    if (context?.appointment === undefined) {
        throw new Error('useAppointmentState must be used within a AppointmentProvider');
    }

    return context.appointment;
}

/** Retrieves the appointment from the closest context */
export function useAppointmentStateUnsafe() {
    return React.useContext(AppointmentStateContext).appointment;
}

/** Whether or not the user has an existing appointment scheduled */
export function useHasAppointment() {
    return React.useContext(AppointmentStateContext).hasAppointment ?? false;
}

/** Whether the mobile check in flow should allow a full check in or end at the pre-check-in complete screen */
export function useIsFullMobileCheckInAllowed() {
    return React.useContext(AppointmentStateContext).isFullMobileCheckInAllowed ?? false;
}

/** Whether the QR code on the outdoor kiosk was scanned to start the check-in/pickup process*/
export function useWasQRScannedAtLocker() {
    return React.useContext(AppointmentStateContext).QRScannedAtLocker ?? false;
}

/** Whether the appointment has a vehicle key on the DMS side */
export function useHasDmsVehicleKey() {
    return !!React.useContext(AppointmentStateContext).appointment?.dmsVehicleKey;
}

/** Whether the appointment has a phone number that should be masked */
export function useMaskPhone() {
    return React.useContext(AppointmentStateContext).maskPhone ?? false;
}

/** Whether the appointment has an email address that should be masked */
export function useMaskEmail() {
    return React.useContext(AppointmentStateContext).maskEmail ?? false;
}

/** Whether the appointment has a VIN that should be masked */
export function useMaskVin() {
    return React.useContext(AppointmentStateContext).maskVin ?? false;
}

/** Actual values submitted in lookup form by the customer */
export function useTypedInFields() {
    return React.useContext(AppointmentStateContext).typedInFields ?? {};
}

/** Whether the appointment is being added right now by a tow company */
export function useTowInProcess() {
    return React.useContext(AppointmentStateContext).towIn ?? false;
}

/** Customer choices for disclaimer pages */
export function useDisclaimerInput() {
    return {
        gaveConsentToSignElectronically: React.useContext(AppointmentStateContext).gaveConsentToSignElectronically,
        termsAcknowledged: React.useContext(AppointmentStateContext).termsAcknowledged,
        retainOldParts: React.useContext(AppointmentStateContext).retainOldParts,
        estimateRequest: React.useContext(AppointmentStateContext).estimateRequest,
        repairCost: React.useContext(AppointmentStateContext).repairCost,
        saveSignature: !React.useContext(AppointmentStateContext).doNotSaveSignature,
    };
}

/** Customer's estimate page signature, separate from this the appointment object saves signatures but that one is the final reciept */
export function useEstimateSignature() {
    return React.useContext(AppointmentStateContext).estimateSignature;
}

/** Returns a dispatch function to update the appointment */
export function useAppointmentDispatch() {
    const context = React.useContext(AppointmentDispatchContext);
    if (context === undefined) {
        throw new Error('useAppointmentState must be used within a AppointmentProvider');
    }

    return context;
}

/** Updates and saves the appointment */
export function useAppointmentUpdateAndPersist(useApptLocale?: boolean) {
    const dispatch = useAppointmentDispatch();
    const interaction = useInteractionDate();
    const { key, dealer: { dmsAppointmentOffsetMinutes: offsetMinutes } } = useKioskState();
    const currentLocale = useLocale();
    const { userID } = useAdminState();
    const { computeNextAvailableCheckinTime } = useKioskTimeTable();
    const { updatePhoneOptInStatus } = useTexting();

    return async (
        appointment: IAppointment | null,
        createIfNoKey = false,
        updateDmsCustomer = false,
        updateDmsVehicle = false,
        persist = true,
        initialInteraction: Date | undefined = undefined,
        allowEmptyingServiceLists: boolean = false,
        useExistingApptDateIfNew = false,
        updateDmsAppointment = false,
        precheckInComplete = false,
        towInComplete = false,
        keyManagement = false,
    ) => {
        if (appointment) {
            let updated = { ...appointment };
            const locale = (!appointment.towInCompanyName) ? currentLocale : Locales.none;
            if (appointment.key) {
                /**
                 * For outdoor kiosks, the key can be inserted into a different kiosk than
                 * the one the check-in started on. The updated kioskKey will be set
                 * into the appoinment record, so use that if it's different.
                 *
                 * A mobile check-in is tied to the dealer's non-physical 'mobile' kiosk,
                 * which the backend cannot resolve for KeyTrak. In that case use the
                 * physical kiosk we're currently on (key) so key handling at an Express/365
                 * kiosk runs against the right kiosk.
                 */
                await updateAppointment(
                    appointment.kioskKey && appointment.kioskKey !== key && appointment.kioskType !== 'mobile' ? appointment.kioskKey : key,
                    appointment,
                    useApptLocale ? appointment.locale : locale,
                    initialInteraction ?? interaction,
                    userID,
                    allowEmptyingServiceLists,
                    updateDmsAppointment,
                    precheckInComplete,
                    towInComplete,
                    keyManagement,
                );
            }
            else if (createIfNoKey) {
                const fullAppointment = {
                    ...emptyAppointment,
                    ...appointment,
                    mobileCheckin: !isElectron(),
                    appointmentDateTime:
                        !useExistingApptDateIfNew && dayjs()
                            .add(offsetMinutes ?? 0, 'minute')
                            .isAfter(appointment.appointmentDateTime)
                            ? computeNextAvailableCheckinTime()
                                ?.toDate() ?? appointment.appointmentDateTime
                            : appointment.appointmentDateTime,
                };
                const appointmentKey = await createAppointment(
                    key,
                    {
                        ...fullAppointment,
                        locale: useApptLocale ? appointment.locale : locale,
                    },
                    initialInteraction ?? interaction,
                );

                if (appointment.optedIntoTextingThisAppointment) {
                    updatePhoneOptInStatus(appointment.phone, true, appointmentKey)
                        .then(() => {
                            insertAuditTrail(appointmentKey, Events.Text_Opt_In);
                        });
                }

                updated = {
                    ...fullAppointment,
                    key: appointmentKey,
                };
            }

            /**
             * If we have a vehicle with VIN and missing Chrome data, attempt to query the
             * server for missing values.
             */
            if (!(appointment.chromeMake && appointment.chromeModel) && appointment.make && appointment.model && appointment.vin && appointment.key) {
                const appt = await getAppointment(appointment.key);
                if (appt !== null) {
                    const { chromeMake, chromeModel } = appt;
                    updated = {
                        ...updated,
                        chromeMake,
                        chromeModel,
                    };
                }
            }

            if (updateDmsCustomer) {
                const dmsCustomerKey = await updateDMSCustomer(key, {
                    ...updated,
                    locale: useApptLocale ? appointment.locale : locale,
                });
                delete updated.oldPhone;
                delete updated.oldEmail;
                updated = {
                    ...updated,
                    dmsCustomerKey,
                    customerInfoUpdated: false,
                };
            }
            if (updateDmsVehicle) {
                const dmsVehicleKey = await updateDMSVehicle(key, updated);
                updated = {
                    ...updated,
                    dmsVehicleKey,
                };
            }

            if (persist) {
                dispatch({
                    type: AppointmentDispatchTypes.Update,
                    payload: updated,
                });
            }

            return updated;
        }
        else {
            return null;
        }
    };
}

export const useClearAppointment = () => {
    const appointment = useAppointmentStateUnsafe();
    const appointmentDispatch = useAppointmentDispatch();
    const resolveRef = useRef<() => void>();

    React.useEffect(() => {
        if (!appointment && resolveRef.current) {
            resolveRef.current();
            resolveRef.current = undefined;
        }
    }, [appointment, resolveRef]);

    return () =>
        new Promise<void>((resolve) => {
            if (appointment) {
                resolveRef.current = resolve;
                appointmentDispatch({ type: AppointmentDispatchTypes.Remove });
            }
            else {
                resolve();
            }
        });
};

const emptyAppointment: IAppointment = {
    id: -1,
    key: '',
    kioskKey: '',
    dealerKey: '',
    adminCreated: false,
    convertedFromDms: false,
    appointmentDateTime: new Date(),
    promiseDateTime: undefined,
    status: 'not scheduled',
    licenseStatus: 'not scanned',
    contactPreference: undefined,
    keyStatus: 'none',
    tagNumber: '',
    city: '',
    email: '',
    firstName: '',
    lastName: '',
    businessName: '',
    make: '',
    mileage: 0,
    model: '',
    chromeMake: null,
    chromeModel: null,
    paymentStatus: 'no payment due',
    phone: '',
    stateOrProvince: '',
    street: '',
    streetLine2: '',
    vin: '',
    year: '',
    postalCode: '',
    servicesConfirmed: [],
    xtimeServices: [],
    qrCode: '',
    mobileCheckInComplete: false,
    mobileCheckInUrl: '',
    allowManualComplete: false,
    locale: Locales.en,
    mobileCheckin: false,
    isHunter: false,
    reminderSent: undefined,
};

// #region Hooks

/**
 * This will create an empty appointment if one does not already exist.
 * @param advisor advisor whether this is an appointment created in admin. Will set the
 * appt date time to now regardless of time tables
 */
export function useEnsureAppointment(advisor?: boolean): {
    appointment: IAppointment | undefined;
    isLoading: boolean;
};
export function useEnsureAppointment(advisor?: boolean) {
    const appointment = useAppointmentStateUnsafe();
    const factory = useEmptyAppointmentFactory(advisor);
    const dispatch = useAppointmentDispatch();

    const [isLoading, setIsLoading] = React.useState(false);
    React.useEffect(() => {
        if (!appointment && !isLoading) {
            try {
                setIsLoading(true);
                console.log('Creating appointment');
                const newAppointment = factory();
                dispatch({
                    type: AppointmentDispatchTypes.Add,
                    payload: newAppointment,
                });
                setIsLoading(false);
            }
            catch (error) {
                console.error(error);
                setIsLoading(false);
            }
        }
    }, [appointment, dispatch, factory, isLoading]);

    return {
        appointment,
        isLoading: isLoading || !appointment,
    };
}

/**
 * Returns a function that generates an empty appointment.
 * @param advisor advisor whether this is an appointment created in admin. Will set the
 * appt date time to now regardless of time tables
 */
export const useEmptyAppointmentFactory = (advisor?: boolean) => {
    const { computeNextAvailableCheckinTime } = useKioskTimeTable();
    const {
        dealer: { key },
    } = useKioskState();

    return (): IAppointment => ({
        ...emptyAppointment,
        dealerKey: key,
        appointmentDateTime: computeNextAvailableCheckinTime(advisor)
            .toDate(),
    });
};

/**
 * Retrieves the valuation data for the current appointment.
 * @param kioskKey The kiosk key
 */
export const useAppointmentValuation = (kioskKey: string) => {
    const appointment = useAppointmentState();
    const dispatch = useAppointmentDispatch();
    const { cleanValue, averageValue, roughValue, vin, mileage } = appointment;
    const { current, next, start, finish } = useStepReducer(['started', 'loading-valuation-data']);
    const valuation: IValuation | undefined =
        roughValue !== undefined && averageValue !== undefined && cleanValue !== undefined ? {
            cleanValue,
            averageValue,
            roughValue,
        } : undefined;
    React.useEffect(() => {
        switch (current) {
            case 'not-started':
                if (!valuation) {
                    start();
                }
                else {
                    finish();
                }
                break;
            case 'started':
                const getValuationData = () => {
                    if (vin) {
                        return getValuationByVin(kioskKey, vin, mileage ?? 0);
                    }

                    finish();

                    return Promise.reject();
                };
                next();
                getValuationData()
                    .then((valuation) => {
                        dispatch({
                            type: AppointmentDispatchTypes.Update,
                            payload: {
                                ...appointment,
                                ...valuation,
                            },
                        });
                    })
                    .catch((_) => null /* suppress reject message */);
                break;
            case 'loading-valuation-data':
                if (valuation) {
                    next();
                }
                break;
        }
    });

    return {
        valuation,
        loading: current !== 'complete',
    };
};

export const useRecordInteraction = (alwaysNew?: boolean) => {
    const dispatch = useAppointmentDispatch();

    return useCallback(
        () => dispatch({ type: alwaysNew ? AppointmentDispatchTypes.RecordNewInitialInteraction : AppointmentDispatchTypes.RecordInitialInteraction }),
        [alwaysNew, dispatch],
    );
};

export const useInteractionDate = () => React.useContext(AppointmentStateContext).initialInteraction;

// #endregion

// #region Texting Hook
export enum ContactPreferenceEnum {
    Email = 'email',
    Phone = 'phone',
    Text = 'text',
    Both = 'both'
}

export enum TextOptInStatus {
    /**
     * User opted out of texting.
     */
    OptedOut = 0,
    /**
     * User opted in to texting.
     */
    OptedIn,
    /**
     * User has not chosen to opt-in or opt-out of texting yet.
     */
    NotDecided
}

/**
 * The object and possible types returned by the `/GetTextOptIn` endpoint.
 */
export interface OptInStatus {
    /**
     * Whether the phone number can receive text messages.
     * `null` is returned by the endpoint if no value is returned due to an error.
     */
    isDeliverable: boolean | null;
    /**
     * The texting opt-in/opt-out status of the phone number returned by the endpoint.
     */
    status: TextOptInStatus;
    /**
     * The phone number that was used to retrieve the other information.
     */
    phone: IAppointment['phone'];
}

/**
 * The object returned by the `useTexting` hook.
 */
export interface TextingReturn {
    /**
     * Function that takes a phone number as potential input, but defaults to the phone number
     * on the current appointment if no number is passed as an argument. The function returns
     * an {@link AxiosResponse} object with the {@link OptInStatus} object as data.
     *
     * The endpoint requires a `dealerKey` and `appointmentKey` but the function retrieves both
     * from the current appointment.
     */
    checkPhoneTextingStatus: GoMotoFunction<
        [string?],
        Promise<AxiosResponse<OptInStatus>>
    >;

    /**
     * Function that takes a phone number and boolean inputs and targets the `/UpsertOptIn` endpoint
     * to update the text opt-in table with the users choice. The boolean represents whether the user
     * chose to opt-in (true) or opt-out (false).
     *
     * Only a status message is returned by the endpoint.
     */
    updatePhoneOptInStatus: GoMotoFunction<
        [string, boolean, string?],
        Promise<AxiosResponse<HttpStatusCode>>
    >;
}

/**
 * Type definition for the `useTexting` hook.
 */
export type TextingHook = GoMotoFunction<[void], TextingReturn>;

/**
 * Takes no props and returns the {@link TextingReturn.checkPhoneTextingStatus} and {@link TextingReturn.updatePhoneOptInStatus}
 * funtions. `checkPhoneTextingStatus` gets the {@link OptInStatus} object from the `/GetTextOptIn` endpoint, and `updatePhoneOptInStatus`
 * updates the table with a new row and whether they opted in/out of texting.
 *
 * If the user is opted out and wants to opt back in, they must text the dealer number with `START`.
 * If the user is opted in and wants to opt out, they must talk with the dealer.
 */
export const useTexting: TextingHook = () => {
    const appointment = useAppointmentStateUnsafe();
    const locale = useLocale();

    const checkPhoneTextingStatus: TextingReturn['checkPhoneTextingStatus'] = async (phone) => {
        const phoneNumber: string = phone ?? (appointment ? appointment.phone : '');

        return await instance.get<OptInStatus>('/GetTextOptIn', {
            params: {
                phone: phoneNumber,
                dealerKey: appointment ? appointment.dealerKey : '',
            },
        });
    };

    const updatePhoneOptInStatus: TextingReturn['updatePhoneOptInStatus'] = async (phone, optIn, appointmentKey = '') => {
        const key: IAppointment['key'] = appointment && !!appointment.key ? appointment.key : appointmentKey;

        return await instance.post<HttpStatusCode>('/UpsertOptIn', {}, {
            params: {
                appointmentKey: key,
                phone: phone,
                optIn: optIn,
                dealerKey: appointment ? appointment.dealerKey : '',
                locale: locale,
            },
        });
    };

    return {
        checkPhoneTextingStatus,
        updatePhoneOptInStatus,
    };
};
// #endregion

// #region AppointmentHistory
/**
 * Takes no props and returns a set of functions that can be used to record the history of an appointment.
 * Currently this does not include the audit trail function, but should include that function in the future
 * so that all functions meant to record the history of an appointment are aggregated in one place.
 *
 * @returns An object of functions to record the history of an appointment.
 */
export const useAppointmentHistory: AppointmentHistoryHook = () => {
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();

    return {
        insertLastPageVisited: (lastPageVisited: string, appointmentKey?: string) => {
            const params: InsertLastPageVisitedParams = {
                lastPageVisited: lastPageVisited,
                appointmentKey: !!appointmentKey ? appointmentKey : appointment?.key ?? '',
            };

            if (!!params.appointmentKey) {
                instance.get('/InsertLastPageVisited', { params });
            }
        },
    };
};
// #endregion

// #region UpdateAppointment
/**
 * Takes in no props and returns an object of functions that are used to update the appointment
 * in our database. These functions are meant to only update small parts of the appointment, so that way they
 * don't make changes or trigger logic that was not meant to happen.
 *
 * @returns An object of functions to update the appointment in our database.
 */
export const useUpdateAppointment: UpdateAppointmentHook = () => {
    const appointment: IAppointment = useAppointmentState();

    return {
        updateSurveyQuestions: ({
            customerDidEnjoy,
            customerWouldRepeat,
        }) => {
            instance.post<void>(
                '/UpdateSurveyQuestions',
                {
                    ...appointment,
                    customerDidEnjoy,
                    customerWouldRepeat,
                },
                {},
            );
        },
    };
};
// #endregion

// #region FormatDateTime Hook
/**
 * Takes no props and returns a set of functions that can be used to format either a passed in date or string
 * to the locale specific date and time formatting.
 *
 * @returns An object of functions to format dates or strings with.
 */
export const useFormatAppointmentDateTime: FormatAppointmentDateTimeHook = () => {
    const { AnErrorHasOccured } = useLocalization();
    const locale: Locales = useLocale();
    const { dealer: { timezone } } = useKioskState();
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();

    const defaultTimeFormats: Record<AppointmentTimeFormatTypes, AppointmentTimeFormats> = {
        [AppointmentTimeFormatTypes.Default]: {
            [Locales.en]: 'dddd, MMMM D, YYYY | h:mm A',
            [Locales.es]: 'dddd D [de] MMMM [de] YYYY | h:mm A',
            [Locales.fr]: 'dddd D MMMM YYYY à H [h] mm',
        },
        [AppointmentTimeFormatTypes.CheckInComplete]: {
            [Locales.en]: 'MMMM D, YYYY - h:mm A',
            [Locales.es]: 'D [de] MMMM [de] YYYY - h:mm A',
            [Locales.fr]: 'D MMMM YYYY à H [h] mm',
        },
        [AppointmentTimeFormatTypes.AppointmentCard]: {
            [Locales.en]: 'MM/DD/YYYY - h:mm A',
            [Locales.es]: 'MM/DD/YYYY - h:mm A',
            [Locales.fr]: 'YYYY/MM/DD - H [h] mm',
        },
    } as const;

    const getAppointmentDateTime: GoMotoFunction<[Date | string], Dayjs> = (appointmentDateTime) => {
        return dayjs.utc(appointmentDateTime);
    };

    const getFormattedAppointmentDateTime: GoMotoFunction<
        [Dayjs, Locales, AppointmentTimeFormats],
        string | null
    > = (appointmentDateTime, locale, timeFormats) => {
        const dealerLocalDateTime: Dayjs = appointmentDateTime.tz(Timezone[timezone]);
        switch (locale) {
            case Locales.en:
                return dealerLocalDateTime.locale('en')
                    .format(timeFormats['en-us']);
            case Locales.es:
                return dealerLocalDateTime.locale('es-us')
                    .format(timeFormats['es-us']);
            case Locales.fr:
                return dealerLocalDateTime.locale('fr')
                    .format(timeFormats['fr-ca'])
                    .toLowerCase();
            case Locales.none:
                return null;
            default:
                return null;
        }
    };

    const formatDateTimeWithTimeFormatType: ReturnType<FormatAppointmentDateTimeHook>['formatDateTimeWithTimeFormatType'] = ({
        appointmentDateTime,
        timeFormatType,
    }) => {
        let appointmentDateTimeValue: Dayjs | null = null;
        let currentTimeFormats: AppointmentTimeFormats | null = null;

        switch (timeFormatType) {
            case AppointmentTimeFormatTypes.CheckInComplete:
                currentTimeFormats = defaultTimeFormats[AppointmentTimeFormatTypes.CheckInComplete];
                break;
            case AppointmentTimeFormatTypes.AppointmentCard:
                currentTimeFormats = defaultTimeFormats[AppointmentTimeFormatTypes.AppointmentCard];
                break;
            case AppointmentTimeFormatTypes.Default:
                currentTimeFormats = defaultTimeFormats[AppointmentTimeFormatTypes.Default];
                break;
            default:
                currentTimeFormats = defaultTimeFormats[AppointmentTimeFormatTypes.Default];
                break;
        }

        if (appointmentDateTime === undefined && appointment !== undefined) {
            appointmentDateTimeValue = getAppointmentDateTime(appointment.appointmentDateTime);
        }
        else if (appointmentDateTime !== undefined) {
            appointmentDateTimeValue = getAppointmentDateTime(appointmentDateTime);
        }
        else {
            return AnErrorHasOccured;
        }

        const formattedAppointmentDateTime: string | null = getFormattedAppointmentDateTime(appointmentDateTimeValue, locale, currentTimeFormats);

        if (formattedAppointmentDateTime === null) {
            return AnErrorHasOccured;
        }
        else {
            return formattedAppointmentDateTime.charAt(0)
                .toUpperCase() + formattedAppointmentDateTime.slice(1);
        }
    };

    const formatDateTimeWithCustomTimeFormat: ReturnType<FormatAppointmentDateTimeHook>['formatDateTimeWithCustomTimeFormat'] = ({
        appointmentDateTime,
        timeFormats,
    }) => {
        let appointmentDateTimeValue: Dayjs | null = null;
        const currentTimeFormats: AppointmentTimeFormats = timeFormats;

        if (appointmentDateTime === undefined && appointment !== undefined) {
            appointmentDateTimeValue = getAppointmentDateTime(appointment.appointmentDateTime);
        }
        else if (appointmentDateTime !== undefined) {
            appointmentDateTimeValue = getAppointmentDateTime(appointmentDateTime);
        }
        else {
            return AnErrorHasOccured;
        }

        const formattedAppointmentDateTime: string | null = getFormattedAppointmentDateTime(appointmentDateTimeValue, locale, currentTimeFormats);

        if (formattedAppointmentDateTime === null) {
            return AnErrorHasOccured;
        }
        else {
            return formattedAppointmentDateTime.charAt(0)
                .toUpperCase() + formattedAppointmentDateTime.slice(1);
        }
    };

    return {
        formatDateTimeWithTimeFormatType,
        formatDateTimeWithCustomTimeFormat,
    };

};
// #endregion

// #region Reducer
type AppointmentPayload = {
    [AppointmentDispatchTypes.Add]: IAppointment;
    [AppointmentDispatchTypes.Remove]: never;
    [AppointmentDispatchTypes.RemoveAndKeepMasks]: never;
    [AppointmentDispatchTypes.RemoveAndKeepInitialInteraction]: never;
    [AppointmentDispatchTypes.RemoveAndRecordInitialInteraction]: never;
    [AppointmentDispatchTypes.Update]: IAppointment;
    [AppointmentDispatchTypes.HasAppointment]: boolean;
    [AppointmentDispatchTypes.RecordInitialInteraction]: never;
    [AppointmentDispatchTypes.RecordNewInitialInteraction]: never;
    [AppointmentDispatchTypes.AllowFullMobileCheckin]: boolean;
    [AppointmentDispatchTypes.QRScannedAtLocker]: boolean;
    [AppointmentDispatchTypes.MaskField]: 'phone' | 'email' | 'vin' | 'all';
    [AppointmentDispatchTypes.UnmaskField]: 'phone' | 'email' | 'vin' | 'all';
    [AppointmentDispatchTypes.TypedInFields]: TypedInFields;
    [AppointmentDispatchTypes.GaveConsentToSignElectronically]: boolean;
    [AppointmentDispatchTypes.TermsAcknowledged]: boolean;
    [AppointmentDispatchTypes.RetainOldParts]: boolean;
    [AppointmentDispatchTypes.EstimateRequest]: 'yes' | 'no' | 'onlyIf';
    [AppointmentDispatchTypes.RepairCost]: number;
    [AppointmentDispatchTypes.SaveSignature]: boolean;
    [AppointmentDispatchTypes.EstimateSignature]: string | undefined;
    [AppointmentDispatchTypes.TowInProcess]: boolean;
    [AppointmentDispatchTypes.RemoveAppointment]: never;
    [AppointmentDispatchTypes.SerializePreCheckIn]: never;
    [AppointmentDispatchTypes.DeserializePreCheckIn]: never;
};
export type AppointmentActions = ActionMap<AppointmentPayload>[keyof ActionMap<AppointmentPayload>];
export const appointmentReducer = (state: InitialStateType, action: AppointmentActions): InitialStateType => {
    switch (action.type) {
        case AppointmentDispatchTypes.Add:
            if (state.appointment) {
                throw new Error('Cannot add an appointment when one already exists');
            }

            return {
                ...state,
                appointment: action.payload,
            };
        case AppointmentDispatchTypes.Remove:
            console.log('Removing Appointment');

            return { ...initialState };
        case AppointmentDispatchTypes.RemoveAppointment:
            console.log('Only Removing Appointment');

            return {
                ...state,
                appointment: initialState.appointment,
            };
        case AppointmentDispatchTypes.RemoveAndKeepMasks:
            console.log('Removing Appointment and keeping masks');

            return {
                ...initialState,
                maskPhone: state.maskPhone,
                maskEmail: state.maskEmail,
                maskVin: state.maskVin,
            };
        case AppointmentDispatchTypes.RemoveAndKeepInitialInteraction:
            console.log('Removing Appointment and keeping initial interaction');

            return {
                ...initialState,
                initialInteraction: state.initialInteraction,
            };
        case AppointmentDispatchTypes.RemoveAndRecordInitialInteraction:
            console.log('Removing Appointment and setting initial interaction');

            return {
                ...initialState,
                initialInteraction: new Date(),
            };
        case AppointmentDispatchTypes.Update:
            return {
                ...state,
                appointment: {
                    ...state.appointment,
                    ...action.payload,
                },
            };
        case AppointmentDispatchTypes.HasAppointment:
            return {
                ...state,
                hasAppointment: action.payload,
            };
        case AppointmentDispatchTypes.RecordInitialInteraction:
            const initialInteraction = state.initialInteraction || new Date();

            return {
                ...state,
                initialInteraction,
            };
        case AppointmentDispatchTypes.RecordNewInitialInteraction:
            return {
                ...state,
                initialInteraction: new Date(),
            };
        case AppointmentDispatchTypes.AllowFullMobileCheckin:
            return {
                ...state,
                isFullMobileCheckInAllowed: action.payload,
            };
        case AppointmentDispatchTypes.QRScannedAtLocker:
            return {
                ...state,
                QRScannedAtLocker: action.payload,
            };
        case AppointmentDispatchTypes.MaskField:
            switch (action.payload) {
                case 'phone':
                    return {
                        ...state,
                        maskPhone: true,
                    };
                case 'email':
                    return {
                        ...state,
                        maskEmail: true,
                    };
                case 'vin':
                    return {
                        ...state,
                        maskVin: true,
                    };
                case 'all':
                    return {
                        ...state,
                        maskPhone: true,
                        maskEmail: true,
                        maskVin: true,
                    };
            }
            break;
        case AppointmentDispatchTypes.UnmaskField:
            switch (action.payload) {
                case 'phone':
                    return {
                        ...state,
                        maskPhone: false,
                    };
                case 'email':
                    return {
                        ...state,
                        maskEmail: false,
                    };
                case 'vin':
                    return {
                        ...state,
                        maskVin: false,
                    };
                case 'all':
                    return {
                        ...state,
                        maskPhone: false,
                        maskEmail: false,
                        maskVin: false,
                    };
            }
            break;
        case AppointmentDispatchTypes.TypedInFields:
            return {
                ...state,
                typedInFields: action.payload,
            };
        case AppointmentDispatchTypes.GaveConsentToSignElectronically:
            if (action.payload && state.appointment) {
                insertAuditTrail(state.appointment.key, Events.Agreed_to_Electronic_Signatures);
            }
            return {
                ...state,
                gaveConsentToSignElectronically: action.payload,
            };
        case AppointmentDispatchTypes.RetainOldParts:
            return {
                ...state,
                retainOldParts: action.payload,
            };
        case AppointmentDispatchTypes.TermsAcknowledged:
            return {
                ...state,
                termsAcknowledged: action.payload,
            };
        case AppointmentDispatchTypes.EstimateRequest:
            return {
                ...state,
                estimateRequest: action.payload,
            };
        case AppointmentDispatchTypes.RepairCost:
            return {
                ...state,
                repairCost: action.payload,
            };
        case AppointmentDispatchTypes.SaveSignature:
            return {
                ...state,
                doNotSaveSignature: !action.payload,
            };
        case AppointmentDispatchTypes.EstimateSignature:
            return {
                ...state,
                estimateSignature: action.payload,
            };
        case AppointmentDispatchTypes.TowInProcess:
            return {
                ...state,
                towIn: action.payload,
            };
        case AppointmentDispatchTypes.SerializePreCheckIn:
            if (state.appointment) {
                return {
                    ...state,
                    appointment: {
                        ...state.appointment,
                        preCheckInData: JSON.stringify({
                            gaveConsentToSignElectronically: state.gaveConsentToSignElectronically,
                            retainOldParts: state.retainOldParts,
                            termsAcknowledged: state.termsAcknowledged,
                            estimateRequest: state.estimateRequest,
                            repairCost: state.repairCost,
                            doNotSaveSignature: state.doNotSaveSignature,
                            estimateSignature: state.estimateSignature,
                        }),
                    },
                };
            }
            else {
                return state;
            }
        case AppointmentDispatchTypes.DeserializePreCheckIn:
            if (state.appointment?.preCheckInData) {
                const {
                    gaveConsentToSignElectronically,
                    retainOldParts,
                    termsAcknowledged,
                    estimateRequest,
                    repairCost,
                    doNotSaveSignature,
                    estimateSignature,
                } = JSON.parse(state.appointment.preCheckInData);

                return {
                    ...state,
                    gaveConsentToSignElectronically,
                    retainOldParts,
                    termsAcknowledged,
                    estimateRequest,
                    repairCost,
                    doNotSaveSignature,
                    estimateSignature,
                };
            }
            else {
                return state;
            }
        default:
            return state;
    }
};

// #endregion

// #region useLockerOperation
/**
 * Unified hook to handle all locker operations possible for both Linux and Mzero kiosks.
 *
 * @param byAdvisor - {@link LockerOperationHook byAdvisor} description.
 *
 * @note For information regarding the return type or hook type definitions, click {@link LockerOperationHook here}.
 */
export const useLockerOperation: LockerOperationHook = ({
    byAdvisor = false,
} = {}) => {
    const appointment: IAppointment | undefined = useAppointmentStateUnsafe();
    const { userID } = useAdminState();
    const {
        findLockerById,
        getNextAvailableLocker,
        retrieveFromLocker,
        storeInLocker,
        exchangeKeyAtLocker,
        openLocker,
    } = useLocker(() => !!appointment && insertAuditTrail(appointment.key, Events.Locker_Open, userID, appointment.keySlotNumber?.toString()));
    const { update } = useKeyTable();
    const updateAppointment = useAppointmentUpdateAndPersist(byAdvisor);
    const dispatch = useAppointmentDispatch();
    const { key } = useKioskState();
    // dynamic set steps based on op type
    const { next, current, start, processing, reset } = useStepReducer(Object.values(LockerOperationSteps));
    const setLockerStatus = useSetLockerStatus();
    const { lockers } = useLockerContext();
    const { api } = useApiCall();

    const initialLockerActionSettings: LockerActionSettings = useMemo(() => ({
        lockerAction: 'not-started',
        locker: undefined,
        error: KeyDropError.none,
        manual: false,
    }), []);

    const [lockerIdOrAppointment, setLockerIdOrAppointment] = useState<AppointmentOrLockerId | undefined>(undefined);
    const [lockerActionSettings, setLockerActionSettings] = useState<LockerActionSettings>(initialLockerActionSettings);

    const resolverRef = useRef<VoidFunction | undefined>(undefined);
    const attemptRef = useRef<number>(1);
    const ignoredLockersRef = useRef<number[]>([]);
    const isMountedRef = useRef<boolean>(true);

    const startLockerOperation: GoMotoFunction<StartProps, Promise<void>> = useCallback(({
        lockerAction,
        lockerId,
        manual = false,
        resetAttempts = false,
        completeAppointment = false,
        appt = undefined,
    }) => {
        return new Promise<void>((resolve, reject) => {
            resolverRef.current = resolve;
            const activeAppointment = appointment ?? appt;

            switch (lockerAction) {
                case 'not-started': {
                    break;
                }
                case 'store-key': {
                    if (appointment !== undefined) {
                        setLockerIdOrAppointment({
                            type: 'appointment',
                            appointment: appointment,
                        });
                        setLockerActionSettings({
                            lockerAction: lockerAction,
                            locker: undefined,
                            error: KeyDropError.none,
                            manual: manual,
                            resetAttempts: resetAttempts,
                        });

                        if (resetAttempts) {
                            attemptRef.current = 1;
                        }
                    }
                    else {
                        reject();
                    }
                    break;
                }
                case 'retrieve-key':
                case 'pickup-and-exchange-key':
                case 'retrieve-exchanged-key':
                case 'open-locker': {
                    if (lockerId !== undefined) {
                        setLockerIdOrAppointment({
                            type: 'lockerId',
                            lockerId: lockerId,
                        });
                        setLockerActionSettings({
                            lockerAction: lockerAction,
                            locker: undefined,
                            error: KeyDropError.none,
                            manual: manual,
                        });
                    }
                    else if (activeAppointment !== undefined) {
                        setLockerIdOrAppointment({
                            type: 'appointment',
                            appointment: {
                                ...activeAppointment,
                                ...(completeAppointment ? {
                                    status: 'completed',
                                } : {}),
                            },
                        });
                        setLockerActionSettings({
                            lockerAction: lockerAction,
                            locker: undefined,
                            error: KeyDropError.none,
                            manual: manual,
                        });
                    }
                    else {
                        reject();
                    }

                    break;
                }
                default: {
                    break;
                }
            }

            start();
        });
    }, [appointment, start]);

    const checkLockerAvailablility: GoMotoFunction<[AppointmentOrLockerId], Promise<boolean>> = useCallback((lockerIdOrAppointment: AppointmentOrLockerId) => {
        return new Promise<boolean>(async (resolve) => {
            if (lockerActionSettings.lockerAction === 'not-started') {
                setLockerActionSettings((prev: LockerActionSettings) => ({
                    ...prev,
                    error: KeyDropError.unknown,
                }));
                resolve(false);
            }
            else if (lockerActionSettings.lockerAction === 'store-key') {
                if (lockerIdOrAppointment.type === 'appointment' && lockerIdOrAppointment.appointment.keyStatus !== 'slot_assigned') {
                    await getNextAvailableLocker(ignoredLockersRef.current)
                        .then((nextAvailableLocker: ILockerFixture | undefined) => {
                            if (nextAvailableLocker !== undefined) {
                                setLockerActionSettings({
                                    ...lockerActionSettings,
                                    locker: nextAvailableLocker,
                                });
                            }
                            else {
                                setLockerActionSettings({
                                    ...lockerActionSettings,
                                    error: KeyDropError.noLockerAvailable,
                                });
                            }

                            resolve(true);
                        });
                }
                else {
                    setLockerActionSettings((prev: LockerActionSettings) => ({
                        ...prev,
                        error: KeyDropError.noLockerAvailable,
                    }));
                    resolve(false);
                }
            }
            else {
                if (lockerIdOrAppointment.type === 'appointment' && lockerIdOrAppointment.appointment.keySlotNumber) {
                    setLockerActionSettings({
                        ...lockerActionSettings,
                        locker: findLockerById(lockerIdOrAppointment.appointment.keySlotNumber),
                    });
                }
                else if (lockerIdOrAppointment.type === 'lockerId' && lockerIdOrAppointment.lockerId) {
                    setLockerActionSettings({
                        ...lockerActionSettings,
                        locker: findLockerById(lockerIdOrAppointment.lockerId),
                    });
                }
                else {
                    setLockerActionSettings({
                        ...lockerActionSettings,
                        error: KeyDropError.noLockerAvailable,
                    });
                }
                resolve(true);
            }
        });
    }, [findLockerById, getNextAvailableLocker, lockerActionSettings]);

    useEffect(() => {
        if (lockerIdOrAppointment && current === LockerOperationSteps.LockerCheck && !lockerActionSettings.locker) {
            const getLocker: GoMotoFunction<[void], Promise<void>> = async () => {
                const isLockerChecked = await checkLockerAvailablility(lockerIdOrAppointment);

                if (isLockerChecked) {
                    next();
                }
            };

            getLocker();
        }
    }, [checkLockerAvailablility, current, lockerActionSettings.locker, lockerIdOrAppointment, next]);

    const [forcedLockerClose, setForcedLockerClose] = useState<boolean>(false);

    const handleStoreKey: GoMotoFunction<void, Promise<void>> = useCallback(async () => {
        const appt: IAppointment | undefined = lockerIdOrAppointment?.type === 'appointment' ? lockerIdOrAppointment.appointment : undefined;

        if (!appt) {
            return;
        }

        /*
         * For a customer who completed check-in on a separate mobile kiosk, the server suppresses
         * the dealer check-in notification at /UpdateAppointment time and expects /AppointmentKeyStored
         * to deliver it once the key reaches a physical kiosk - that single call also promotes the
         * appointment's kioskKey to this kiosk, updates the keyTable with the appointmentKey association,
         * sets keyStatus to slot_assigned, and inserts the CustomerKeyLoaded audit-trail event.
         *
         * Advisor key storage and same-kiosk customer check-ins fall through to the existing
         * /UpdateKeySlot + /UpdateAppointment flow, which already does the right thing.
         */
        const checkedInOnDifferentKiosk: boolean = !!appt.kioskKey && appt.kioskKey !== key;

        if (!byAdvisor && appt.mobileCheckInComplete && checkedInOnDifferentKiosk) {
            const slot: IKeySlot = await appointmentKeyStored(
                key,
                appt.key,
                lockerActionSettings.locker!.Id,
            );

            const reopenRequested: boolean = appt.keyStatus === 'reopen requested dropoff';

            dispatch({
                type: AppointmentDispatchTypes.Update,
                payload: {
                    ...appt,
                    keyStatus: reopenRequested ? 'reopen requested dropoff' : 'slot_assigned',
                    keySlot: slot.id,
                    keySlotNumber: slot.keySlotNumber,
                    storedByAdvisor: false,
                    kioskKey: key,
                    status: 'scheduled',
                    allowManualComplete: false,
                },
            });

            return;
        }

        const storedKeyTable: IKeyTable = await update(lockerActionSettings.locker!.Id, byAdvisor, appt.key);
        const updated: IAppointment = {
            ...appt,
            keyStatus: 'slot_assigned',
            keySlot: storedKeyTable.id,
            keySlotNumber: storedKeyTable.keySlotNumber,
            storedByAdvisor: byAdvisor,
            kioskKey: key,
            status: 'scheduled',
        };
        await updateAppointment(updated);
    }, [byAdvisor, dispatch, key, lockerActionSettings.locker, lockerIdOrAppointment, update, updateAppointment]);

    const handleRetrieveKey: GoMotoFunction<[boolean | ILockerFixture], Promise<void>> = useCallback((results: boolean | ILockerFixture) => {
        return new Promise<void>(async (resolve) => {
            if (lockerIdOrAppointment) {
                if (results === false) {
                    setLockerActionSettings((prev: LockerActionSettings) => ({
                        ...prev,
                        error: KeyDropError.unknown,
                    }));
                }
                else {
                    await update(lockerActionSettings.locker!.Id, byAdvisor);

                    if (lockerIdOrAppointment.type === 'appointment') {
                        const updated: IAppointment = {
                            ...lockerIdOrAppointment.appointment,
                            keyStatus: lockerActionSettings.manual ? 'none' : 'picked up',
                            keySlot: undefined,
                            keySlotNumber: undefined,
                            storedByAdvisor: byAdvisor,
                            kioskKey: key,
                            paymentStatus: (
                                byAdvisor
                                    && lockerIdOrAppointment.appointment.paymentStatus === 'not paid' ? 'marked paid' : lockerIdOrAppointment.appointment.paymentStatus
                            ),
                            serviceTotal: byAdvisor && lockerIdOrAppointment.appointment.paymentStatus === 'not paid' ? 0 : lockerIdOrAppointment.appointment.serviceTotal,
                        };
                        await updateAppointment(updated, false, false, false, true, undefined, false, false, false, false, false, true);
                    }
                }
            }

            resolve();
        });
    }, [byAdvisor, key, lockerActionSettings.locker, lockerActionSettings.manual, lockerIdOrAppointment, update, updateAppointment]);

    const handlePickupAndExchangeKey: GoMotoFunction<[boolean | ILockerFixture], Promise<void>> = useCallback((results: boolean | ILockerFixture) => {
        return new Promise<void>(async (resolve) => {
            if (results === false) {
                setLockerActionSettings((prev: LockerActionSettings) => ({
                    ...prev,
                    error: KeyDropError.failedToOpen,
                }));
            }
            else {
                if (lockerIdOrAppointment?.type === 'appointment') {
                    const updated: IAppointment = {
                        ...lockerIdOrAppointment.appointment,
                        keyStatus: lockerIdOrAppointment.appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? 'loaner key stored' : 'rental key stored',
                        status: lockerIdOrAppointment.appointment.returningLoanerOrRental === AppointmentLoanerOrRentalOption.Loaner ? 'loaner returned' : 'rental returned',
                    };
                    await updateAppointment(updated);
                }
            }

            resolve();
        });
    }, [lockerIdOrAppointment, updateAppointment]);

    const handleRetrieveExchangedKey: GoMotoFunction<[boolean | ILockerFixture], Promise<void>> = useCallback((results: boolean | ILockerFixture) => {
        return new Promise<void>(async (resolve) => {
            if (results === false) {
                setLockerActionSettings((prev: LockerActionSettings) => ({
                    ...prev,
                    error: KeyDropError.unknown,
                }));
            }
            else {
                // TODO: Make this an object of props rather than function arguments
                await update(lockerActionSettings.locker!.Id, byAdvisor);
                if (lockerIdOrAppointment?.type === 'appointment') {
                    const updated: IAppointment = {
                        ...lockerIdOrAppointment.appointment,
                        keyStatus: 'picked up',
                        keySlot: undefined,
                        keySlotNumber: undefined,
                        storedByAdvisor: false,
                        kioskKey: key,
                        status: 'completed',
                    };
                    await updateAppointment(updated);
                }
                else {
                    setLockerActionSettings((prev: LockerActionSettings) => ({
                        ...prev,
                        error: KeyDropError.unknown,
                    }));
                }
            }

            resolve();
        });
    }, [byAdvisor, key, lockerActionSettings.locker, lockerIdOrAppointment, update, updateAppointment]);

    const forceLockerClose: GoMotoFunction<void, Promise<void>> = () => {
        return new Promise<void>(async (resolve) => {
            if (current === LockerOperationSteps.LockerClosed && !forcedLockerClose) {
                if (lockerIdOrAppointment && lockerActionSettings.locker) {
                    setForcedLockerClose(true);
                    const locker = lockers?.find((locker) => locker.Id === lockerActionSettings.locker?.Id);

                    switch (lockerActionSettings.lockerAction) {
                        case 'store-key':
                            await handleStoreKey();
                            if (locker?.Status === 'Available') {
                                setLockerStatus(locker, 'Taken');
                            }
                            break;
                        case 'retrieve-key':
                            await handleRetrieveKey(true);
                            if (locker?.Status === 'Taken') {
                                setLockerStatus(locker, 'Available');
                            }
                            break;
                        case 'pickup-and-exchange-key':
                            await handlePickupAndExchangeKey(true);
                            if (locker?.Status === 'Taken') {
                                setLockerStatus(locker, 'Available');
                            }
                            break;
                        case 'retrieve-exchanged-key':
                            await handleRetrieveExchangedKey(true);
                            if (locker?.Status === 'Taken') {
                                setLockerStatus(locker, 'Available');
                            }
                            break;
                        default:
                            break;
                    }
                }
            }

            next();
            resolve();
        });
    };

    useEffect(() => {
        if (current === LockerOperationSteps.LockerOpen) {
            if (lockerIdOrAppointment && lockerActionSettings.locker) {
                next();

                switch (lockerActionSettings.lockerAction) {
                    case 'store-key': {
                        if (lockerIdOrAppointment.type === 'appointment') {
                            const lockerId = lockerActionSettings.locker.Id;
                            const appointmentKey = lockerIdOrAppointment.appointment.key;
                            void storeInLocker(lockerId, appointmentKey)
                                .catch((error: unknown) => {
                                    if (error !== 'TimeOut') {
                                        void update(lockerId, byAdvisor, undefined);
                                        ignoredLockersRef.current.push(lockerActionSettings.locker!.Id);

                                        if (attemptRef.current < 2) {
                                            attemptRef.current++;
                                            reset();

                                            throw new Error('RETRY');
                                        }
                                        else {
                                            throw new Error('FAILED_TO_OPEN');
                                        }
                                    }

                                    throw error;
                                })
                                .then(async () => {
                                    if (!forcedLockerClose) {
                                        await handleStoreKey();
                                    }
                                })
                                .then(() => {
                                    next();
                                })
                                .catch((error: unknown) => {
                                    if (typeof error === 'object' && !!error && 'message' in error) {
                                        if (error.message === 'RETRY') {
                                            return;
                                        }

                                        if (isMountedRef.current) {
                                            if (error.message === 'FAILED_TO_OPEN') {
                                                setLockerActionSettings((prev: LockerActionSettings) => ({
                                                    ...prev,
                                                    error: KeyDropError.failedToOpen,
                                                }));
                                            }
                                            else {
                                                setLockerActionSettings((prev: LockerActionSettings) => ({
                                                    ...prev,
                                                    error: KeyDropError.unknown,
                                                }));
                                            }
                                        }
                                    }
                                    else {

                                    }
                                });
                        }
                        break;
                    }
                    case 'retrieve-key': {
                        const appointmentKey: IAppointment['key'] | undefined = lockerIdOrAppointment.type === 'appointment' ? lockerIdOrAppointment.appointment.key : undefined;

                        /*
                         * Fire audit/KeyTrak at locker-open time so possession is recorded the moment
                         * the advisor can grab the key, not after they close the door. The follow-up
                         * /UpdateKeySlot + /UpdateAppointment calls on close persist the actual state.
                         */
                        const updatedAppointment: IAppointment | object = lockerIdOrAppointment.type === 'appointment' ? {
                            ...lockerIdOrAppointment.appointment,
                            keyStatus: lockerActionSettings.manual ? 'none' : 'picked up',
                            keySlot: undefined,
                            keySlotNumber: undefined,
                            storedByAdvisor: byAdvisor,
                            kioskKey: key,
                            paymentStatus: (
                                byAdvisor
                                    && lockerIdOrAppointment.appointment.paymentStatus === 'not paid' ? 'marked paid' : lockerIdOrAppointment.appointment.paymentStatus
                            ),
                            serviceTotal: byAdvisor && lockerIdOrAppointment.appointment.paymentStatus === 'not paid' ? 0 : lockerIdOrAppointment.appointment.serviceTotal,
                        } : {};

                        const recordOpenQuery: string = byAdvisor && userID
                            ? `keySlotNumber=${lockerActionSettings.locker.Id}&userID=${userID}`
                            : `keySlotNumber=${lockerActionSettings.locker.Id}`;

                        api<void>({})
                            .post(`RecordOpenLocker?${recordOpenQuery}`, updatedAppointment);

                        retrieveFromLocker(lockerActionSettings.locker.Id, appointmentKey)
                            .then(async (result: boolean | ILockerFixture) => {
                                if (!forcedLockerClose) {
                                    await handleRetrieveKey(result);
                                }
                            })
                            .then(() => {
                                next();
                            })
                            .catch(() => {
                                setLockerActionSettings((prev: LockerActionSettings) => ({
                                    ...prev,
                                    error: KeyDropError.unknown,
                                }));
                            })
                            .finally(() => {
                                setLockerIdOrAppointment(undefined);
                                setLockerActionSettings(initialLockerActionSettings);
                            });
                        break;
                    }
                    case 'pickup-and-exchange-key': {
                        const appointmentKey: IAppointment['key'] | undefined = lockerIdOrAppointment.type === 'appointment' ? lockerIdOrAppointment.appointment.key : undefined;

                        const updatedAppointment: IAppointment | object = lockerIdOrAppointment.type === 'appointment' ? {
                            ...lockerIdOrAppointment.appointment,
                            keyStatus: 'picked up',
                            keySlot: undefined,
                            keySlotNumber: undefined,
                            storedByAdvisor: false,
                            kioskKey: key,
                            status: 'completed',
                        } : {};

                        api<void>({})
                            .post(`RecordOpenLocker?keySlotNumber=${lockerActionSettings.locker.Id}`, updatedAppointment);

                        exchangeKeyAtLocker(lockerActionSettings.locker.Id, appointmentKey)
                            .then(async (results: boolean | ILockerFixture) => {
                                if (!forcedLockerClose) {
                                    handlePickupAndExchangeKey(results);
                                }
                            })
                            .then(() => {
                                next();
                            })
                            .catch(() => {
                                setLockerActionSettings((prev: LockerActionSettings) => ({
                                    ...prev,
                                    error: KeyDropError.unknown,
                                }));
                            })
                            .finally(() => {
                                setLockerIdOrAppointment(undefined);
                                setLockerActionSettings(initialLockerActionSettings);
                            });
                        break;
                    }
                    case 'retrieve-exchanged-key': {
                        const appointmentKey: IAppointment['key'] | undefined = lockerIdOrAppointment.type === 'appointment' ? lockerIdOrAppointment.appointment.key : undefined;

                        /*
                         * Fire audit/KeyTrak at locker-open time so the loaner/rental retrieval is recorded the moment
                         * the advisor can grab the key, not after they close the door. The follow-up /UpdateAppointment
                         * call on close persists the completed state and skips the duplicate audit insert.
                         */
                        const updatedAppointment: IAppointment | object = lockerIdOrAppointment.type === 'appointment' ? {
                            ...lockerIdOrAppointment.appointment,
                            keyStatus: 'picked up',
                            keySlot: undefined,
                            keySlotNumber: undefined,
                            storedByAdvisor: false,
                            kioskKey: key,
                        } : {};

                        const recordOpenQuery: string = byAdvisor && userID
                            ? `keySlotNumber=${lockerActionSettings.locker.Id}&userID=${userID}`
                            : `keySlotNumber=${lockerActionSettings.locker.Id}`;

                        api<void>({})
                            .post(`RecordOpenLocker?${recordOpenQuery}`, updatedAppointment);

                        retrieveFromLocker(lockerActionSettings.locker.Id, appointmentKey)
                            .then(async (results: boolean | ILockerFixture) => {
                                if (!forcedLockerClose) {
                                    handleRetrieveExchangedKey(results);
                                }
                            })
                            .then(() => {
                                next();
                            })
                            .catch(() => {
                                setLockerActionSettings((prev: LockerActionSettings) => ({
                                    ...prev,
                                    error: KeyDropError.unknown,
                                }));
                            })
                            .finally(() => {
                                setLockerIdOrAppointment(undefined);
                                setLockerActionSettings(initialLockerActionSettings);
                            });
                        break;
                    }
                    case 'open-locker': {
                        openLocker(lockerActionSettings.locker.Id)
                            .then(() => {
                                next();
                            })
                            .catch((error: string) => {
                                if (error.includes('stderr')) {
                                    setLockerActionSettings((prev: LockerActionSettings) => ({
                                        ...prev,
                                        error: KeyDropError.failedToOpen,
                                    }));
                                }
                                else {
                                    setLockerActionSettings((prev: LockerActionSettings) => ({
                                        ...prev,
                                        error: KeyDropError.unknown,
                                    }));
                                }
                            });
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
        }
    }, [
        api,
        byAdvisor,
        current,
        exchangeKeyAtLocker,
        forcedLockerClose,
        handlePickupAndExchangeKey,
        handleRetrieveExchangedKey,
        handleRetrieveKey,
        handleStoreKey,
        initialLockerActionSettings,
        key,
        lockerActionSettings.locker,
        lockerActionSettings.lockerAction,
        lockerActionSettings.manual,
        lockerIdOrAppointment,
        next,
        openLocker,
        reset,
        retrieveFromLocker,
        storeInLocker,
        update,
        userID,
    ]);

    useEffect(() => {
        if (current === 'complete') {
            if (resolverRef.current) {
                resolverRef.current();
            }

            resolverRef.current = undefined;
            setLockerIdOrAppointment(undefined);
            setForcedLockerClose(false);
            setLockerActionSettings(initialLockerActionSettings);
        }
    }, [current, initialLockerActionSettings, resolverRef]);

    useEffect(() => {
        if (
            (
                (lockerActionSettings.lockerAction === 'store-key' && processing)
                || (lockerActionSettings.lockerAction !== 'store-key' && lockerIdOrAppointment)
            )
            && lockerActionSettings.error !== KeyDropError.none
        ) {
            reset();
            resolverRef.current = undefined;
            setLockerIdOrAppointment(undefined);
            setForcedLockerClose(false);
            setLockerActionSettings(initialLockerActionSettings);
        }
    }, [initialLockerActionSettings, lockerActionSettings.error, lockerActionSettings.lockerAction, lockerIdOrAppointment, processing, reset]);

    return {
        start: startLockerOperation,
        inProgress: processing,
        complete: current === 'complete',
        hasOpened: current === LockerOperationSteps.LockerClosed || current === 'complete',
        locker: lockerActionSettings.locker,
        forceLockerClose: forceLockerClose,
        error: lockerActionSettings.error ?? KeyDropError.none,
    };
};
// #endregion
